'use client'

import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import type { PlanChanges } from '@/lib/billing/plan-changes'
import type { PlanConflictError } from '@/lib/billing/plans/plan-write'
import { describePlanChanges, describePlanValues, formatPlanTimestamp } from './change-format'
import { ChangeList } from './change-list'
import { PlanDetails } from './plan-details'
import type { PlanPayload } from './types'

export interface PlanReview {
  /** Exactly what "Confirm" sends. */
  payload: PlanPayload
  /** `null` for a new plan. */
  changes: PlanChanges | null
}

interface ReviewDialogProps {
  review: PlanReview | null
  saving: boolean
  error: string | null
  /** Set on a 409; only a reload can continue. */
  conflict: PlanConflictError | null
  onConfirm: () => void
  onBack: () => void
  onReload: () => void
}

export function ReviewDialog({ review, saving, error, conflict, onConfirm, onBack, onReload }: ReviewDialogProps) {
  const changes = review?.changes ?? null
  function keepOpenWhileSaving(event: Event) {
    if (saving) event.preventDefault()
  }
  return (
    <Dialog open={review !== null} onOpenChange={open => { if (!open && !saving) onBack() }}>
      <DialogContent className="sm:max-w-2xl" onEscapeKeyDown={keepOpenWhileSaving} onInteractOutside={keepOpenWhileSaving}>
        {review ? <>
          <DialogHeader>
            <DialogTitle>{changes ? 'Review changes' : 'Review new plan'}</DialogTitle>
            <DialogDescription>
              {changes ? 'Only these settings change. Nothing is saved until you confirm.' : 'Check every setting before creating the plan.'}
            </DialogDescription>
          </DialogHeader>
          {changes ? <>
            <ChangeList rows={describePlanChanges(changes)} />
            <details className="rounded-md border p-3">
              <summary className="cursor-pointer text-sm font-semibold">Full plan</summary>
              <div className="mt-3"><PlanDetails rows={describePlanValues(review.payload)} /></div>
            </details>
          </> : <PlanDetails rows={describePlanValues(review.payload)} />}
          {error ? <p role="alert" className="rounded-md border border-destructive p-3 text-sm text-destructive">{error}</p> : null}
          {conflict ? <ConflictNotice conflict={conflict} /> : null}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={onBack}>Back to editing</Button>
            {conflict
              ? <Button type="button" variant="destructive" onClick={onReload}>Discard my changes and reload</Button>
              : <Button type="button" disabled={saving} onClick={onConfirm}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {/* Own element: a translated label beside the spinner crashes React. */}
                <span>{changes ? 'Confirm and save' : 'Confirm and create'}</span>
              </Button>}
          </DialogFooter>
        </> : null}
      </DialogContent>
    </Dialog>
  )
}

function ConflictNotice({ conflict }: { conflict: PlanConflictError }) {
  const when = conflict.changedAt ? ` at ${formatPlanTimestamp(conflict.changedAt)}` : ''
  return (
    <div role="alert" className="space-y-1 rounded-md border border-destructive p-3 text-sm">
      <p className="font-medium">{`Changed by ${conflict.changedBy ?? 'another process'}${when} after you opened it.`}</p>
      <p className="text-muted-foreground">Your draft is kept. To save, reload the latest plan and apply your changes again.</p>
    </div>
  )
}
